// Drop-in replacement for navigator.geolocation.getCurrentPosition that adds a
// single automatic retry. Same (success, error, options) signature, so call
// sites only swap the function name.
//
// Why: the first attempt typically uses enableHighAccuracy (GPS), which on
// phones/laptops can be slow or intermittently time out — users were being
// dropped to the fallback location on a single hiccup. If the first attempt
// fails with TIMEOUT or POSITION_UNAVAILABLE we retry once with more forgiving
// options: coarse (network/wifi) location, a longer timeout, and a cached fix
// allowed. That succeeds in the vast majority of the "failed once" cases.
// PERMISSION_DENIED is never retried (retrying can't help and re-prompts).
export function geolocateWithRetry(
  onSuccess: PositionCallback,
  onError: PositionErrorCallback,
  options?: PositionOptions
): void {
  if (typeof navigator === 'undefined' || !('geolocation' in navigator) || !navigator.geolocation) {
    onError({
      code: 2, // POSITION_UNAVAILABLE
      message: 'Geolocation not supported',
      PERMISSION_DENIED: 1,
      POSITION_UNAVAILABLE: 2,
      TIMEOUT: 3,
    } as GeolocationPositionError);
    return;
  }

  const primaryOptions: PositionOptions = {
    enableHighAccuracy: true,
    timeout: 10000,
    maximumAge: 300000, // 5 min
    ...(options || {}),
  };

  // Forgiving retry: coarse location is faster and far more reliable than GPS.
  const retryOptions: PositionOptions = {
    enableHighAccuracy: false,
    timeout: 20000,
    maximumAge: 600000, // accept a fix up to 10 min old
  };

  navigator.geolocation.getCurrentPosition(
    onSuccess,
    (err) => {
      // A denied permission won't be fixed by retrying.
      if (err && err.code === err.PERMISSION_DENIED) {
        onError(err);
        return;
      }
      console.warn(
        `📍 Geolocation attempt 1 failed (code ${err && err.code}: ${err && err.message}) — retrying with coarse location…`
      );
      navigator.geolocation.getCurrentPosition(onSuccess, onError, retryOptions);
    },
    primaryOptions
  );
}
